import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import { RegistrationState } from '@/models';
import type { ErrorModel } from '@/models';
import { CategoryService, ErrorService, NnnRegistrationService, NnnService } from '@/services';
import type { NicInterventieSummary, NnnRegistrationWithRows, NocZorgresultaatSummary } from '@/services';
import { NnnRegistrationRowTableComponent } from '../nnn-registration-row-table/nnn-registration-row-table.component';
import type { NandaDiagnoseView, NnnInstrumentOptionGroup } from '../../types/careplan-nnn.types';
import { buildInstrumentOptionGroups } from '../../utils/build-instrument-option-groups';
import { buildNandaDiagnoseIndex } from '../../utils/build-nanda-diagnose-index';

/**
 * Read-only view of one final careplan-NNN registration, used outside the
 * careplan-NNN page (case-task submissions). Loads the registration and the
 * NNN reference indexes itself so embedders only need ids.
 */
@Component({
  selector: 'app-nnn-registration-preview',
  standalone: true,
  imports: [SharedModule, NnnRegistrationRowTableComponent],
  templateUrl: './nnn-registration-preview.component.html',
  styleUrl: './nnn-registration-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnRegistrationPreviewComponent implements OnInit {
  private readonly registrationService = inject(NnnRegistrationService);
  private readonly nnnService = inject(NnnService);
  private readonly categoryService = inject(CategoryService);
  private readonly errorService = inject(ErrorService);

  readonly patientId = input.required<string>();
  readonly registrationId = input.required<string>();
  readonly careInstitutionId = input<string | null>(null);

  protected readonly finalState = RegistrationState.FINAL;
  protected readonly loading = signal(true);
  protected readonly registrations = signal<NnnRegistrationWithRows[]>([]);
  protected readonly nandaIndex = signal<Map<string, NandaDiagnoseView>>(new Map());
  protected readonly nocIndex = signal<Map<string, NocZorgresultaatSummary>>(new Map());
  protected readonly nicIndex = signal<Map<string, NicInterventieSummary>>(new Map());
  protected readonly appFunctionGroups = signal<NnnInstrumentOptionGroup[]>([]);

  ngOnInit(): void {
    void this.load();
  }

  private async load(): Promise<void> {
    await Promise.all([
      this.loadRegistration(),
      this.loadNandaIndex(),
      this.loadNocIndex(),
      this.loadNicIndex(),
      this.loadAppFunctions(),
    ]);
    this.loading.set(false);
  }

  private async loadRegistration(): Promise<void> {
    const { data } = await this.registrationService.getRegistrationById(this.patientId(), this.registrationId());
    this.registrations.set(data ? [data] : []);
  }

  private async loadNandaIndex(): Promise<void> {
    const [diagnoses, domeinen, klassen] = await Promise.all([
      this.nnnService.getNandaDiagnoses(),
      this.nnnService.getNandaDomeinen(),
      this.nnnService.getNandaKlassen(),
    ]);
    if (!diagnoses.data) {
      return;
    }
    this.nandaIndex.set(buildNandaDiagnoseIndex(diagnoses.data, domeinen.data ?? [], klassen.data ?? []));
  }

  private async loadNocIndex(): Promise<void> {
    const { data } = await this.nnnService.getNocOutcomes();
    if (data) {
      this.nocIndex.set(new Map(data.map(zorgresultaat => [zorgresultaat.id, zorgresultaat])));
    }
  }

  private async loadNicIndex(): Promise<void> {
    const { data } = await this.nnnService.getNicInterventions();
    if (data) {
      this.nicIndex.set(new Map(data.map(interventie => [String(interventie.code), interventie])));
    }
  }

  private async loadAppFunctions(): Promise<void> {
    const careInstitutionId = this.careInstitutionId();
    if (!careInstitutionId) {
      return;
    }
    try {
      const categories = await firstValueFrom(this.categoryService.getCategoriesForInstitutionId(careInstitutionId));
      this.appFunctionGroups.set(buildInstrumentOptionGroups(categories));
    } catch (error) {
      this.errorService.onError(error as ErrorModel);
    }
  }
}
